import { createContext, useContext, useState, ReactNode, Dispatch, SetStateAction } from "react";
import { MRT_PaginationState, MRT_SortingState } from "material-react-table";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { useGridColumns, GridColumnsType } from "src/components/Grid/hooks/columnsExtension/useGridColumns.tsx";
import { useDataListValueOverridesColumns } from "./useDataListValueOverridesColumns";
import {
    DataListValueOverride,
    useGetAllDataListValueOverride,
} from "src/api/client-api/dataListValueOverride/useGetAllDataListValueOverride";
import {
    DataListVersionWithDataList,
    useGetAllDataListVersions,
} from "src/api/sure-api/dataList/useGetAllDataListVersions";

type DataListsValuesOverridesContextInitialValues = {
    page: MRT_PaginationState;
    setPage: Dispatch<SetStateAction<MRT_PaginationState>>;

    sorting: MRT_SortingState;
    setSorting: Dispatch<SetStateAction<MRT_SortingState>>;

    totalItems: number | undefined;

    overrides: DataListValueOverride[] | undefined;
    dataListVersions: DataListVersionWithDataList[] | undefined;
    mrt_columns: GridColumnsType<DataListValueOverride>;
    isLoading: boolean;

    search: string;
    delayedSearch: string;
    setSearch: (arg1: string) => void;
    resetSearch: () => void;

    // Modal states
    showCreateModal: boolean;
    setShowCreateModal: Dispatch<SetStateAction<boolean>>;

    gridKey: string;
};

const DataListsValuesOverridesContext = createContext<DataListsValuesOverridesContextInitialValues | undefined>(
    undefined,
);

type DataListsValuesOverridesContextProviderProps = {
    children: ReactNode;
};

export const DataListsValuesOverridesContextProvider = (props: DataListsValuesOverridesContextProviderProps) => {
    const { children } = props;

    const gridKey = "settings-data-lists-values-overrides-grid";

    const [page, setPage] = useState<MRT_PaginationState>({
        pageIndex: 0,
        pageSize: 20,
    });
    const [sorting, setSorting] = useState<MRT_SortingState>([{ id: "id", desc: false }]);

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);

    const search = useDelayedSearch();
    const { overrides, isLoading, totalItems } = useGetAllDataListValueOverride({
        page: page.pageIndex + 1,
        itemsPerPage: page.pageSize,
        search: search.delayedSearch,
        sorting: sorting,
    });

    const overridesDataListVersionIds = overrides?.map(
        (override: DataListValueOverride): number => override.dataListVersionId,
    );

    const { dataListVersions } = useGetAllDataListVersions({
        ids: overridesDataListVersionIds,
        enabled: overrides && overrides.length > 0,
    });

    const mrt_overridesColumns = useDataListValueOverridesColumns(dataListVersions);
    const mrt_columns = useGridColumns<DataListValueOverride>(mrt_overridesColumns, gridKey);

    const items: DataListsValuesOverridesContextInitialValues = {
        mrt_columns,

        page,
        setPage,
        totalItems,

        sorting,
        setSorting,

        overrides,
        dataListVersions,
        isLoading,

        search: search.search,
        delayedSearch: search.delayedSearch,
        setSearch: search.setSearch,
        resetSearch: search.resetSearch,

        // Modal states
        showCreateModal,
        setShowCreateModal,

        gridKey: gridKey,
    };

    return (
        <DataListsValuesOverridesContext.Provider value={items}>{children}</DataListsValuesOverridesContext.Provider>
    );
};

export const useDataListsValuesOverridesContext = () => {
    const context = useContext(DataListsValuesOverridesContext);
    if (context === undefined) {
        throw new Error(
            "useDataListsValuesOverridesContext must be used within an DataListsValuesOverridesContextProvider",
        );
    }
    return context;
};
